import React, { useState } from 'react';
import { Button, Flex, Heading, Select, Text } from '@chakra-ui/react';
import { FiTrash } from 'react-icons/fi';
import { useBroxelPaymentLinks } from './hooks/useBroxelPaymentLinks';
import { PaymentLinksTable } from './components/PaymentLinksTable';
import { PaymentLinksFilters, PaymentLinksTableFilters } from './components/PaymentLinksFilters';
import { ColumnsMenu, defaultVisibleColumns } from './components/ColumnsMenu';

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];

/**
 * Contenedor de la vista de listado de ligas de pago (Consulta). Compone el hook de consumo
 * (`useBroxelPaymentLinks` → GET bff/payment-links-view/broxel/raw) con el panel de filtros
 * (`PaymentLinksFilters`) y la tabla presentacional. Los filtros (concepto, monto min/máx,
 * estado y rango de fecha) se resuelven en el BACKEND: cada cambio reinicia a la primera
 * página y vuelve a consultar, por lo que la paginación corresponde al conjunto filtrado.
 * El filtro de Estado usa el catálogo `PAYMENT_LINK_STATUSES` (Pendiente/Aprobado/Cancelado),
 * que son los `status_concept` que devuelve Broxel. Sin filtros por default: arranca mostrando
 * todos los links (paginados) y el usuario aplica los filtros que quiera.
 */
const PaymentLinksView = () => {
  const {
    data, loading, error, nextPage, prevPage, queryParams,
    setPageSize, setFilter, setDateRange, clearFilters,
  } = useBroxelPaymentLinks();

  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns());

  // Fuente única de verdad: los filtros que se muestran vienen de los query params vigentes.
  const filters: PaymentLinksTableFilters = {
    concept: queryParams.concept,
    status: queryParams.status,
    amountMin: queryParams.amountMin,
    amountMax: queryParams.amountMax,
    dateStart: queryParams.dateStart,
    dateEnd: queryParams.dateEnd,
  };

  return (
    <>
      <Text color="neutral.600" fontSize="s">Links de Pago</Text>

      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap="l"
        marginBottom="xl"
      >
        <Heading as="h2" fontSize="xl">Links de Pago</Heading>

        <Flex alignItems="center" gap="l">
          <Button color="primary.800" variant="ghost" onClick={clearFilters}>
            <FiTrash fontSize="1.2em" />
            <Text marginLeft="l">Limpiar filtros</Text>
          </Button>

          <ColumnsMenu visibleColumns={visibleColumns} onChange={setVisibleColumns} />

          <Text fontSize="s" color="neutral.600">Mostrar:</Text>
          <Select
            size="sm"
            width="6em"
            value={queryParams.size}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </Select>
        </Flex>
      </Flex>

      <PaymentLinksFilters
        filters={filters}
        onConceptChange={(value) => setFilter('concept', value)}
        onStatusChange={(value) => setFilter('status', value)}
        onAmountMinChange={(value) => setFilter('amountMin', value)}
        onAmountMaxChange={(value) => setFilter('amountMax', value)}
        onDateRangeChange={(range) => setDateRange(range.start, range.end)}
      />

      <PaymentLinksTable
        data={data}
        loading={loading}
        error={error}
        visibleColumns={visibleColumns}
        onNextPage={nextPage}
        onPreviousPage={prevPage}
      />
    </>
  );
};

export default PaymentLinksView;
